import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 280px;
  background: #000000;
  color: white;
  transition: transform 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
`;

const UserRole = styled.div`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
`;

const MenuList = styled.nav`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const MenuSection = styled.div`
  margin-bottom: 1.5rem;
`;

const MenuSectionTitle = styled.div`
  padding: 0.75rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 1rem 0;
`;

const MenuItem = styled.div<{ active?: boolean }>`
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.active ? '#ffffff' : 'transparent'};
  display: flex;
  align-items: center;
  gap: 0.875rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const MenuIcon = styled.span`
  font-size: 1.25rem;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuText = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
`;

const SidebarFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const adminMenuItems = [
    { path: '/admin/members', icon: '👥', label: '회원 명부' },
    { path: '/admin/member-applications', icon: '📝', label: '신규가입 관리' },
    { path: '/admin/schedule', icon: '🏐', label: '운동 일정' },
    { path: '/admin/fees', icon: '💰', label: '회비 관리' },
    { path: '/admin/uniforms', icon: '👕', label: '유니폼 관리' },
    { path: '/admin/competitions', icon: '🏆', label: '대회 일정' },
    { path: '/admin/guests', icon: '👋', label: '게스트 관리' },
    { path: '/admin/notices', icon: '📢', label: '공지사항 관리' },
    { path: '/admin/events', icon: '🎉', label: '이벤트 관리' },
    { path: '/admin/dormant', icon: '😴', label: '휴면 관리' },
    { path: '/admin/menu-settings', icon: '⚙️', label: '메뉴 관리' },
  ];
  
  const [enabledMenus, setEnabledMenus] = useState<Array<{key: string; name: string; icon: string; path: string}>>([]);

  // 활성화된 메뉴 조회 (회원용 - 관리자도 회원 메뉴를 보여주기 위해 필요)
  useEffect(() => {
    const fetchEnabledMenus = async () => {
      try {
        const response = await apiClient.get('/menus/enabled');
        if (response.data.success) {
          setEnabledMenus(response.data.data);
        }
      } catch (error) {
        console.error('활성화된 메뉴 조회 실패:', error);
        // 실패 시 기본 메뉴 사용
        setEnabledMenus([
          { key: 'schedule', name: '운동 일정', icon: '🏐', path: '/member/schedule' },
          { key: 'fees', name: '회비', icon: '💰', path: '/member/fees' },
          { key: 'uniforms', name: '유니폼', icon: '👕', path: '/member/uniforms' },
          { key: 'competitions', name: '대회', icon: '🏆', path: '/member/competitions' },
          { key: 'events', name: '이벤트', icon: '🎉', path: '/member/events' },
          { key: 'guest-apply', name: '게스트 신청', icon: '👋', path: '/member/guest-apply' },
          { key: 'notices', name: '공지사항', icon: '📢', path: '/member/notices' },
          { key: 'mypage', name: '마이페이지', icon: '👤', path: '/member/mypage' },
        ]);
      }
    };

    fetchEnabledMenus();
  }, []);
  
  // 회원용 메뉴 (활성화된 메뉴만)
  const memberMenuItems = enabledMenus.map(menu => ({
    path: menu.path,
    icon: menu.icon,
    label: menu.name
  }));
  
  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose();
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };
  
  const handleLogoClick = () => {
    if (user?.is_admin) {
      navigate('/admin');
    } else {
      navigate('/member');
    }
    onClose();
  };
  
  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader>
          <Logo onClick={handleLogoClick}>H_B</Logo>
          <UserInfo>
            <UserName>{user?.name || '사용자'}</UserName>
            <UserRole>{user?.is_admin ? '관리자' : '일반 회원'}</UserRole>
          </UserInfo>
        </SidebarHeader>
        
        <MenuList>
          {user?.is_admin ? (
            <>
              <MenuSection>
                <MenuSectionTitle>관리자 메뉴</MenuSectionTitle>
                {adminMenuItems.map(item => (
                  <MenuItem
                    key={item.path}
                    active={location.pathname === item.path}
                    onClick={() => handleMenuClick(item.path)}
                  >
                    <MenuIcon>{item.icon}</MenuIcon>
                    <MenuText>{item.label}</MenuText>
                  </MenuItem>
                ))}
              </MenuSection>
              
              <MenuDivider />
              
              <MenuSection>
                <MenuSectionTitle>회원 메뉴</MenuSectionTitle>
                {memberMenuItems.map(item => (
                  <MenuItem
                    key={item.path}
                    active={location.pathname === item.path}
                    onClick={() => handleMenuClick(item.path)}
                  >
                    <MenuIcon>{item.icon}</MenuIcon>
                    <MenuText>{item.label}</MenuText>
                  </MenuItem>
                ))}
              </MenuSection>
            </>
          ) : (
            <MenuSection>
              {memberMenuItems.map(item => (
                <MenuItem
                  key={item.path}
                  active={location.pathname === item.path}
                  onClick={() => handleMenuClick(item.path)}
                >
                  <MenuIcon>{item.icon}</MenuIcon>
                  <MenuText>{item.label}</MenuText>
                </MenuItem>
              ))}
            </MenuSection>
          )}
        </MenuList>
        
        <SidebarFooter>
          <LogoutButton onClick={handleLogout}>
            로그아웃
          </LogoutButton>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};

export default AdminSidebar;

